/**
 * MapView — Full-bleed Mapbox GL JS map canvas.
 *
 * Renders GeoJSON layers (features, infrastructure, parcels), handles
 * hover/selection paint transitions, flyTo animations, right-click context
 * menu, import preview, and drag-and-drop device linking.
 */

import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MAPPER_BASEMAP_STYLE } from './mapBasemapStyle';
import { useMapStore } from './MapContext';
import {
  MAPPER_BOUNDARY_ZOOM_THRESHOLD,
  MAPPER_CADASTRAL_DISPLAY_MIN_ZOOM,
  MAPPER_CADASTRAL_LABEL_MIN_ZOOM,
  MAPPER_CADASTRAL_PMTILES_PATH,
} from './constants';
import saAdminBoundaries from './sa-admin-boundaries.json';
import { useMapLayers, useMapFeatures, useMapInfrastructure, useMapParcelsByIds, useLinkInfraDevice, useGeeJob, useLookupParcelByPoint, fetchMapParcelById, fetchParcelOverlayGeoJSON } from './api';
import { CATEGORY_COLORS, CATEGORY_LABELS, ALL_CATEGORIES } from './types';
import type { MapLayer, MapFeature, MapInfrastructure, MapParcel, InfraCategoryType } from './types';
import { Leaf, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';
import { useAppStore } from '@/store/appStore';

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

function hasRenderable(item: { geometry?: unknown }): boolean {
  const g = item.geometry as Record<string, unknown> | null | undefined;
  return Boolean(g && typeof g === 'object' && g.type && g.coordinates);
}

function featuresToGeoJSON(features: MapFeature[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: features.filter(hasRenderable).map((f) => ({
      type: 'Feature' as const,
      id: f.id,
      geometry: f.geometry as GeoJSON.Geometry,
      properties: {
        _id: f.id,
        _name: f.name,
        _layerId: f.layer_id,
        _status: f.status,
        _source: f.source,
        _area_m2: f.area_m2,
        _featureType: f.feature_type,
        ...f.properties,
      },
    })),
  };
}

function infrastructureToGeoJSON(items: MapInfrastructure[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: items.filter(hasRenderable).map((inf) => ({
      type: 'Feature' as const,
      id: inf.id,
      geometry: inf.geometry as GeoJSON.Geometry,
      properties: {
        _id: inf.id,
        _name: inf.name,
        _infra_type: inf.infra_type,
        _category: inf.category,
        _subcategory: inf.subcategory || '',
        _status: inf.status,
        _source: inf.source,
        _color: CATEGORY_COLORS[inf.category as InfraCategoryType]?.hex || '#6b7280',
        _assetBacked: Boolean(inf.farm_product_id),
      },
    })),
  };
}

function parcelsToGeoJSON(parcels: MapParcel[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: parcels.filter(hasRenderable).map((parcel) => {
      const displayName = getParcelDisplayName(parcel);
      const areaHa = Number(parcel.area_ha);
      return {
        type: 'Feature' as const,
        id: parcel.id,
        geometry: parcel.geometry as GeoJSON.Geometry,
        properties: {
          _id: parcel.id,
          _name: displayName,
          _label: displayName,
          _feature_name: parcel.feature_name || null,
          _external_id: parcel.external_id || parcel.natural_key_value || null,
          _sg_number: parcel.sg_number || null,
          _farm_name: parcel.farm_name || null,
          _erf_number: parcel.erf_number || null,
          _land_type: parcel.land_type || null,
          _area_ha: Number.isFinite(areaHa) ? areaHa : 0,
          linked_entity_id: parcel.linked_entity_id || null,
        },
      };
    }),
  };
}

function getParcelDisplayName(parcel: Pick<MapParcel, 'id' | 'feature_name' | 'farm_name' | 'external_id' | 'sg_number' | 'erf_number' | 'prcl_key' | 'natural_key_value' | 'land_type'>): string {
  const featureName = String(parcel.feature_name || '').trim();
  const farmName = String(parcel.farm_name || '').trim();
  const externalId = String(parcel.external_id || parcel.natural_key_value || '').trim();
  const sgNumber = String(parcel.sg_number || '').trim();
  const erfNumber = String(parcel.erf_number || '').trim();
  const parcelKey = String(parcel.prcl_key || '').trim();
  const landType = String(parcel.land_type || '').trim().toLowerCase();
  if (featureName) return featureName;
  if (erfNumber && (landType === 'erf' || !farmName)) return `Erf ${erfNumber}`;
  return farmName || externalId || (erfNumber ? `Erf ${erfNumber}` : sgNumber || parcelKey || parcel.id);
}

function compareParcelSpecificity(a: MapParcel, b: MapParcel): number {
  const aArea = Number.isFinite(a.area_ha) ? a.area_ha : Number.POSITIVE_INFINITY;
  const bArea = Number.isFinite(b.area_ha) ? b.area_ha : Number.POSITIVE_INFINITY;
  if (aArea !== bArea) return aArea - bArea;

  const aHasNaturalKey = Boolean(String(a.natural_key || a.natural_key_value || '').trim());
  const bHasNaturalKey = Boolean(String(b.natural_key || b.natural_key_value || '').trim());
  if (aHasNaturalKey !== bHasNaturalKey) return aHasNaturalKey ? -1 : 1;

  return String(a.id || '').localeCompare(String(b.id || ''));
}

function pickPreferredParcel(parcels: MapParcel[]): MapParcel | null {
  if (!Array.isArray(parcels) || parcels.length === 0) return null;
  return [...parcels].sort(compareParcelSpecificity)[0] || null;
}

function mergeSelectedParcels(parcels: MapParcel[], selectedParcels: MapParcel[]): MapParcel[] {
  if (selectedParcels.length === 0) return parcels;
  const existingIds = new Set(parcels.map((parcel) => parcel.id));
  const missingSelections = selectedParcels.filter((parcel) => !existingIds.has(parcel.id));
  return missingSelections.length > 0 ? [...missingSelections, ...parcels] : parcels;
}

function getBoundsCenter(bounds: [number, number, number, number]): [number, number] {
  const [w, s, e, n] = bounds;
  return [(w + e) / 2, (s + n) / 2];
}

const VIEWPORT_SYNC_DEBOUNCE_MS = 150;

function getViewportBboxPrecision(zoom: number): number {
  if (!Number.isFinite(zoom)) return 3;
  if (zoom < 12) return 2;
  if (zoom < 15) return 3;
  return 4;
}

function roundViewportEdge(value: number, precision: number, direction: 'down' | 'up'): number {
  const factor = 10 ** precision;
  return direction === 'down'
    ? Math.floor(value * factor) / factor
    : Math.ceil(value * factor) / factor;
}

function buildViewportQueryState(bounds: maplibregl.LngLatBounds, zoom: number): { bbox: string; zoom: number } {
  const precision = getViewportBboxPrecision(zoom);
  const west = roundViewportEdge(bounds.getWest(), precision, 'down');
  const south = roundViewportEdge(bounds.getSouth(), precision, 'down');
  const east = roundViewportEdge(bounds.getEast(), precision, 'up');
  const north = roundViewportEdge(bounds.getNorth(), precision, 'up');
  return {
    bbox: `${west},${south},${east},${north}`,
    zoom: Number(zoom.toFixed(1)),
  };
}

function getMapperTileRequestHeaders(): Record<string, string> {
  const authState = useAuthStore.getState();
  const appState = useAppStore.getState();
  let token = String(authState.token || '').trim();
  let tenant = String(authState.selectedTenant || '').trim();
  let appId = String(appState.appId || '').trim();

  if (typeof window !== 'undefined') {
    try {
      const rawAuth = localStorage.getItem('dc.auth');
      if (rawAuth) {
        const parsed = JSON.parse(rawAuth);
        if (!token) token = String(parsed?.token || '').trim();
        if (!tenant) tenant = String(parsed?.tenant || parsed?.tenantId || '').trim();
        if (!appId) appId = String(parsed?.app_id || parsed?.appId || '').trim();
      }
      if (!appId) {
        appId = String(localStorage.getItem('dc.app_id') || '').trim();
      }
    } catch {
      // ignore storage parsing issues and fall back to in-memory state
    }
  }

  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (tenant) headers['x-tenant-id'] = tenant;
  if (appId) headers['x-app-id'] = appId;
  return headers;
}

function isMapperTileUrl(url: string): boolean {
  try {
    const resolved = typeof window !== 'undefined'
      ? new URL(url, window.location.origin)
      : new URL(url);
    return resolved.pathname.includes('/cp/mapper/tiles/');
  } catch {
    return String(url || '').includes('/cp/mapper/tiles/');
  }
}

/** MapLibre worker fetches fail on root-relative tile URLs; must be absolute. */
function getMapperTileUrlTemplate(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/cp/mapper/tiles/{z}/{x}/{y}.pbf`;
  }
  return '/cp/mapper/tiles/{z}/{x}/{y}.pbf';
}

function getCadastralPmtilesUrl(): string {
  const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  return `pmtiles://${origin}${MAPPER_CADASTRAL_PMTILES_PATH}`;
}

/** CSG-style resolve-in fade (z9→z12) multiplied by a per-layer opacity `factor`.
 *  MapLibre rejects a 'zoom' expression nested inside ['*', …] — it may only be the
 *  top-level input to step/interpolate — so the zoom interpolate stays top-level and
 *  the factor is folded into each output stop. Mathematically identical to the old
 *  fade(z) * factor (factor is zoom-independent, so linear interpolation commutes). */
function cadastralFadeOpacity(
  factor: maplibregl.ExpressionSpecification | number,
): maplibregl.ExpressionSpecification {
  return [
    'interpolate', ['linear'], ['zoom'],
    MAPPER_CADASTRAL_DISPLAY_MIN_ZOOM, 0,
    MAPPER_CADASTRAL_DISPLAY_MIN_ZOOM + 1, ['*', factor, 0.4],
    MAPPER_CADASTRAL_LABEL_MIN_ZOOM, ['*', factor, 0.75],
    MAPPER_BOUNDARY_ZOOM_THRESHOLD, factor,
  ] as maplibregl.ExpressionSpecification;
}

/** Parcel labels resolve in from z11. The old labelFade had an INVALID duplicate stop
 *  at z12 (LABEL_MIN_ZOOM+1 === BOUNDARY_ZOOM_THRESHOLD === 12) and was wrapped in
 *  ['*', …, 0.85]. The 0.85 is folded into the outputs and stops are made strictly
 *  ascending (z11→z13). Peak opacity 0.85, same fade-in shape as intended. */
function cadastralLabelFadeOpacity(): maplibregl.ExpressionSpecification {
  return [
    'interpolate', ['linear'], ['zoom'],
    MAPPER_CADASTRAL_LABEL_MIN_ZOOM, 0,
    MAPPER_CADASTRAL_LABEL_MIN_ZOOM + 1, 0.72,
    MAPPER_CADASTRAL_LABEL_MIN_ZOOM + 2, 0.85,
  ];
}

async function probeCadastralPmtilesAvailable(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  try {
    const resp = await fetch(`${window.location.origin}${MAPPER_CADASTRAL_PMTILES_PATH}`, {
      method: 'HEAD',
      cache: 'no-store',
    });
    return resp.ok;
  } catch {
    return false;
  }
}

let pmtilesProtocolSingleton: Protocol | null = null;

function ensurePmtilesProtocolRegistered() {
  if (pmtilesProtocolSingleton) return pmtilesProtocolSingleton;
  pmtilesProtocolSingleton = new Protocol();
  maplibregl.addProtocol('pmtiles', pmtilesProtocolSingleton.tile);
  return pmtilesProtocolSingleton;
}

function getSafeFitPadding(map: maplibregl.Map, requested = 80) {
  const container = map.getContainer();
  const width = container?.clientWidth || 0;
  const height = container?.clientHeight || 0;
  const horizontal = Math.min(requested, Math.max(16, Math.floor((width - 48) / 2)));
  const vertical = Math.min(requested, Math.max(16, Math.floor((height - 48) / 2)));
  return {
    top: vertical,
    right: horizontal,
    bottom: vertical,
    left: horizontal,
  };
}

function fitBoundsSafely(
  map: maplibregl.Map,
  bounds: [number, number, number, number],
  options?: {
    duration?: number;
    curve?: number;
    maxZoom?: number;
  },
): boolean {
  const [w, s, e, n] = bounds;
  if (![w, s, e, n].every((value) => Number.isFinite(value))) return false;

  const width = map.getContainer()?.clientWidth || 0;
  const height = map.getContainer()?.clientHeight || 0;
  if (width < 96 || height < 96) return false;
  if (Math.abs(e - w) < 1e-9 && Math.abs(n - s) < 1e-9) return false;

  try {
    const camera = map.cameraForBounds([[w, s], [e, n]], {
      padding: getSafeFitPadding(map),
      maxZoom: options?.maxZoom ?? 17,
    });
    if (!camera?.center || !Number.isFinite(camera.center.lng) || !Number.isFinite(camera.center.lat)) {
      return false;
    }
    map.flyTo({
      ...camera,
      duration: options?.duration ?? 2500,
      curve: options?.curve ?? 1.42,
      essential: true,
    });
    return true;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

interface MapViewProps {
  visibleObjectTypes?: string[];
}

export default function MapView({ visibleObjectTypes }: MapViewProps = {}) {
  const queryClient = useQueryClient();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const parcelViewportSyncTimeoutRef = useRef<number | null>(null);
  const hoveredFeatureId = useRef<string | null>(null);
  const hoveredSourceId = useRef<string | null>(null);
  const hoveredSourceLayerId = useRef<string | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const listenersAttached = useRef(new Set<string>());
  const lastGeeTileUrlRef = useRef<string | null>(null);
  const parcelsRef = useRef<MapParcel[]>([]);
  const selectedParcelIdsRef = useRef<Set<string>>(new Set());
  const guidedParcelSelectionActiveRef = useRef(false);
  const [currentZoom, setCurrentZoom] = useState(5);
  const previousZoomRef = useRef(5);
  const boundaryAutoVisibilityRef = useRef(false);
  const cadastralUsesLegacyTilesRef = useRef(false);
  const cadastralSourceProbeRef = useRef<boolean | null>(null);
  const parcelOverlaySyncTimeoutRef = useRef<number | null>(null);
  const [cadastralStaticHint, setCadastralStaticHint] = useState<string | null>(null);
  const [cadastralProbeDone, setCadastralProbeDone] = useState(false);

  const BOUNDARY_ZOOM_THRESHOLD = MAPPER_BOUNDARY_ZOOM_THRESHOLD;
  const CADASTRAL_DISPLAY_MIN_ZOOM = MAPPER_CADASTRAL_DISPLAY_MIN_ZOOM;
  const CADASTRAL_LABEL_MIN_ZOOM = MAPPER_CADASTRAL_LABEL_MIN_ZOOM;

  const {
    map: storeMap,
    setMap,
    setSelectedFeature,
    setSelectedInfrastructure,
    setActiveParcel,
    activeParcel,
    activeFarmGroup,
    selectedParcels,
    toggleParcelSelection,
    clearParcelSelection,
    editingInfrastructureGeometryId,
    geeOverlay,
    updateGeeOverlay,
    clearGeeOverlay,
    setContextMenu,
    flyToTarget,
    clearFlyTo,
    importPreview,
    importStep,
    showBoundaries,
    setShowBoundaries,
    setParcelInspectorOpen,
    guidedParcelSelectionActive,
    setGuidedParcelSelectionParcel,
    setParcelViewport,
    parcelLayerScope,
    assetLayerMode,
  } = useMapStore();
  const hasSelectedParcels = selectedParcels.length > 0;
  const farmOnlyMode = parcelLayerScope === 'farm_only' && Boolean(activeFarmGroup?.parcel_ids?.length);
  const activeFarmParcelIds = useMemo(
    () => (activeFarmGroup?.parcel_ids ?? [])
      .map((parcelId) => String(parcelId || '').trim())
      .filter(Boolean),
    [activeFarmGroup?.parcel_ids]
  );
  const activeFarmParcelIdsKey = activeFarmParcelIds.join('|');

  const parcelSelectionActive = guidedParcelSelectionActive;
  const shouldShowParcelLayers = farmOnlyMode || showBoundaries || parcelSelectionActive || hasSelectedParcels;

  // Data queries — no farm-first unlock gate; layers always load when session is ready
  const { data: layerData } = useMapLayers({ enabled: true });
  const { data: featureData } = useMapFeatures({ limit: 1000, enabled: true });
  const { data: infraData } = useMapInfrastructure({ limit: 500, enabled: true });
  const farmParcelsQuery = useMapParcelsByIds(activeFarmGroup?.parcel_ids || [], { enabled: farmOnlyMode });
  const linkDevice = useLinkInfraDevice();
  const lookupParcelByPoint = useLookupParcelByPoint();
  const geeJob = useGeeJob(geeOverlay?.job_id, {
    enabled: geeOverlay?.status === 'pending',
    pollWhilePending: true,
  });

  const layers = layerData?.layers ?? [];
  const features = featureData?.features ?? [];
  const allInfrastructure = (infraData?.items ?? []).filter((item) => item.id !== editingInfrastructureGeometryId);
  const assetBackedInfrastructure = allInfrastructure.filter((item) => Boolean(item.farm_product_id));
  const baseInfrastructure = assetLayerMode === 'asset_only' ? assetBackedInfrastructure : allInfrastructure;
  const visibleObjectTypeSet = visibleObjectTypes?.length ? new Set(visibleObjectTypes) : null;
  const infrastructure = visibleObjectTypeSet
    ? baseInfrastructure.filter((item) => {
        const presetKey = String(item.attributes?.mapper_object_preset || '').trim();
        return (
          visibleObjectTypeSet.has(String(item.category || '').trim())
          || visibleObjectTypeSet.has(String(item.infra_type || '').trim())
          || (presetKey ? visibleObjectTypeSet.has(presetKey) : false)
        );
      })
    : baseInfrastructure;
  const farmParcels = farmOnlyMode ? (farmParcelsQuery.data ?? []) : [];
  const knownParcels = mergeSelectedParcels(farmParcels, selectedParcels);

  useEffect(() => {
    parcelsRef.current = knownParcels;
  }, [knownParcels]);

  useEffect(() => {
    selectedParcelIdsRef.current = new Set(selectedParcels.map((parcel) => parcel.id));
  }, [selectedParcels]);

  useEffect(() => {
    guidedParcelSelectionActiveRef.current = guidedParcelSelectionActive;
  }, [guidedParcelSelectionActive]);

  useEffect(() => {
    const previousZoom = previousZoomRef.current;
    const crossedIntoBoundaryZoom = previousZoom < BOUNDARY_ZOOM_THRESHOLD && currentZoom >= BOUNDARY_ZOOM_THRESHOLD;
    const crossedOutOfBoundaryZoom = previousZoom >= BOUNDARY_ZOOM_THRESHOLD && currentZoom < BOUNDARY_ZOOM_THRESHOLD;
    const canAutoManageBoundaries = !farmOnlyMode && !guidedParcelSelectionActive && !activeParcel && !hasSelectedParcels;

    if (!canAutoManageBoundaries) {
      boundaryAutoVisibilityRef.current = false;
      previousZoomRef.current = currentZoom;
      return;
    }

    if (crossedIntoBoundaryZoom && !showBoundaries) {
      boundaryAutoVisibilityRef.current = true;
      setShowBoundaries(true);
    } else if (crossedOutOfBoundaryZoom) {
      if (boundaryAutoVisibilityRef.current && showBoundaries) {
        setShowBoundaries(false);
      }
      boundaryAutoVisibilityRef.current = false;
    } else if (!showBoundaries) {
      boundaryAutoVisibilityRef.current = false;
    }

    previousZoomRef.current = currentZoom;
  }, [
    activeParcel,
    farmOnlyMode,
    currentZoom,
    guidedParcelSelectionActive,
    hasSelectedParcels,
    setShowBoundaries,
    showBoundaries,
  ]);

  const clearGuidedParcelSelection = useCallback(() => {
    clearParcelSelection();
    setGuidedParcelSelectionParcel(null);
    setShowBoundaries(true);
    setParcelInspectorOpen(false);
    toast.info('Parcel deselected. Click again to choose another parcel.', { duration: 2000 });
  }, [clearParcelSelection, setGuidedParcelSelectionParcel, setParcelInspectorOpen, setShowBoundaries]);

  const confirmGuidedParcelSelection = useCallback((parcel: MapParcel) => {
    if (activeParcel?.id === parcel.id) {
      clearGuidedParcelSelection();
      return;
    }
    setActiveParcel(parcel);
    setGuidedParcelSelectionParcel(parcel);
    setShowBoundaries(true);
    setParcelInspectorOpen(true);
    toast.success(`Parcel matched: ${getParcelDisplayName(parcel)}`, { duration: 2200 });
  }, [activeParcel?.id, clearGuidedParcelSelection, setActiveParcel, setGuidedParcelSelectionParcel, setParcelInspectorOpen, setShowBoundaries]);

  const resolveParcelById = useCallback(async (parcelId: string): Promise<MapParcel | null> => {
    const normalizedParcelId = String(parcelId || '').trim();
    if (!normalizedParcelId) return null;

    const existing = parcelsRef.current.find((parcel) => parcel.id === normalizedParcelId) || null;
    if (existing) return existing;

    try {
      return await queryClient.fetchQuery({
        queryKey: ['mapper', 'parcels', 'detail', normalizedParcelId],
        queryFn: ({ signal }) => fetchMapParcelById(normalizedParcelId, signal),
        staleTime: 15_000,
      });
    } catch {
      return null;
    }
  }, [queryClient]);

  const toggleResolvedParcelSelection = useCallback(async (parcelId: string) => {
    const matched = await resolveParcelById(parcelId);
    if (!matched) {
      toast.error('Could not load that farm boundary right now.');
      return;
    }

    const wasSelected = selectedParcelIdsRef.current.has(matched.id);
    toggleParcelSelection(matched);
    toast[wasSelected ? 'info' : 'success'](
      `${wasSelected ? 'Parcel deselected' : 'Parcel selected'}: ${getParcelDisplayName(matched)}`,
      { duration: 2000 }
    );
  }, [resolveParcelById, toggleParcelSelection]);

  useEffect(() => {
    if (!storeMap) return;

    const handleGuidedParcelLookup = (e: maplibregl.MapMouseEvent) => {
      if (!parcelSelectionActive) return;
      if (lookupParcelByPoint.isPending) return;

      const fallbackLookup = () => {
        lookupParcelByPoint.mutate(
          { lng: e.lngLat.lng, lat: e.lngLat.lat },
          {
            onSuccess: (parcel) => {
              if (!parcel) {
                toast.error('No farm parcel matched that point. Click closer to the farm or use the fallback search list.');
                return;
              }
              confirmGuidedParcelSelection(parcel);
            },
            onError: () => {
              toast.error('Could not match that point to a farm parcel right now.');
            },
          }
        );
      };

      const interactiveParcelLayers = ['parcels-fill', 'parcels-line'].filter((layerId) => Boolean(storeMap.getLayer(layerId)));
      if (interactiveParcelLayers.length > 0) {
        const clickedFeatureIds = Array.from(new Set(storeMap
          .queryRenderedFeatures(e.point, { layers: interactiveParcelLayers })
          .map((feature) => {
            return String(feature.properties?._id || feature.id || '').trim();
          })
          .filter(Boolean)));
        const clickedParcels = clickedFeatureIds
          .map((featureId) => parcelsRef.current.find((parcel) => parcel.id === featureId) || null)
          .filter((parcel): parcel is MapParcel => Boolean(parcel));
        const matchedParcel = pickPreferredParcel(clickedParcels);
        if (matchedParcel) {
          confirmGuidedParcelSelection(matchedParcel);
          return;
        }
        const clickedFeatureId = clickedFeatureIds[0];
        if (clickedFeatureId) {
          void resolveParcelById(clickedFeatureId).then((parcel) => {
            if (parcel) {
              confirmGuidedParcelSelection(parcel);
              return;
            }
            fallbackLookup();
          });
          return;
        }
      }

      fallbackLookup();
    };

    storeMap.on('click', handleGuidedParcelLookup);
    return () => {
      storeMap.off('click', handleGuidedParcelLookup);
    };
  }, [confirmGuidedParcelSelection, lookupParcelByPoint, parcelSelectionActive, resolveParcelById, storeMap]);

  /* ---------------------------------------------------------------- */
  /*  Clear hovered state helper                                        */
  /* ---------------------------------------------------------------- */
  const clearHover = useCallback((map: maplibregl.Map) => {
    if (hoveredFeatureId.current && hoveredSourceId.current) {
      try {
        map.setFeatureState(
          {
            source: hoveredSourceId.current,
            ...(hoveredSourceLayerId.current ? { sourceLayer: hoveredSourceLayerId.current } : {}),
            id: hoveredFeatureId.current,
          },
          { hover: false },
        );
      } catch { /* source may have been removed */ }
      hoveredFeatureId.current = null;
      hoveredSourceId.current = null;
      hoveredSourceLayerId.current = null;
    }
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Initialize map                                                    */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAPPER_BASEMAP_STYLE,
      center: [25.0, -30.5], // South Africa
      zoom: 5,
      pitch: 0,
      bearing: 0,
      attributionControl: true,
      antialias: true,
      transformRequest: (url) => {
        if (!isMapperTileUrl(url)) return { url };
        return {
          url,
          headers: getMapperTileRequestHeaders(),
        };
      },
    });

    // Controls
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'bottom-right');
    map.addControl(new maplibregl.ScaleControl({ maxWidth: 200, unit: 'metric' }), 'bottom-left');
    map.addControl(new maplibregl.FullscreenControl(), 'bottom-right');

    const syncParcelViewport = ({ debounce = false }: { debounce?: boolean } = {}) => {
      const updateViewport = () => {
        parcelViewportSyncTimeoutRef.current = null;
        const b = map.getBounds();
        if (!b) return;
        const zoom = map.getZoom();
        const viewportState = buildViewportQueryState(b, zoom);
        setParcelViewport(viewportState.bbox, viewportState.zoom);
        setCurrentZoom(zoom);
      };
      if (debounce) {
        if (parcelViewportSyncTimeoutRef.current !== null) {
          window.clearTimeout(parcelViewportSyncTimeoutRef.current);
        }
        parcelViewportSyncTimeoutRef.current = window.setTimeout(updateViewport, VIEWPORT_SYNC_DEBOUNCE_MS);
        return;
      }
      if (parcelViewportSyncTimeoutRef.current !== null) {
        window.clearTimeout(parcelViewportSyncTimeoutRef.current);
        parcelViewportSyncTimeoutRef.current = null;
      }
      updateViewport();
    };
    let resizeFrame: number | null = null;
    const syncMapLayout = () => {
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame);
      }
      resizeFrame = window.requestAnimationFrame(() => {
        map.resize();
        syncParcelViewport();
      });
    };

    map.on('load', () => {
      mapRef.current = map;
      setMap(map);
      syncParcelViewport();
    });

    map.on('error', (event) => {
      const sourceId = String((event as { sourceId?: string }).sourceId || '');
      if (sourceId !== 'parcels-source' || cadastralUsesLegacyTilesRef.current) return;
      cadastralUsesLegacyTilesRef.current = true;
      setCadastralStaticHint('Static cadastral tiles failed to load — using live fallback.');
      console.error('[mapper] parcels-source error; switching to legacy tiles', event);
      try {
        const layerIds = ['parcels-active-label', 'parcels-label', 'parcels-selected-line', 'parcels-line', 'parcels-glow', 'parcels-halo', 'parcels-fill'];
        for (const lid of layerIds) {
          if (map.getLayer(lid)) map.removeLayer(lid);
        }
        if (map.getSource('parcels-source')) map.removeSource('parcels-source');
      } catch {
        /* best-effort cleanup before legacy re-add */
      }
      setCadastralProbeDone(true);
    });

    map.on('moveend', () => {
      syncParcelViewport({ debounce: true });
    });

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
        syncMapLayout();
      })
      : null;
    if (resizeObserver && containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener('resize', syncMapLayout);

    // Right-click context menu
    map.on('contextmenu', (e) => {
      e.preventDefault();
      setContextMenu({
        x: e.point.x,
        y: e.point.y,
        lngLat: [e.lngLat.lng, e.lngLat.lat],
      });
    });

    // Close context menu on click
    map.on('click', () => {
      setContextMenu(null);
    });

    return () => {
      if (parcelViewportSyncTimeoutRef.current !== null) {
        window.clearTimeout(parcelViewportSyncTimeoutRef.current);
        parcelViewportSyncTimeoutRef.current = null;
      }
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame);
      }
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncMapLayout);
      map.remove();
      mapRef.current = null;
      listenersAttached.current.clear();
      setMap(null);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------------------------------------------------------- */
  /*  Helper: add interactive listeners to a Mapbox layer               */
  /* ---------------------------------------------------------------- */
  const addLayerListeners = useCallback((
    map: maplibregl.Map,
    layerId: string,
    sourceId: string,
    onClick: (fid: string, lngLat: maplibregl.LngLat) => void,
    labelFn: (props: Record<string, unknown>) => string,
    sourceLayerId?: string,
  ) => {
    if (listenersAttached.current.has(layerId)) return;
    if (!map.getLayer(layerId)) return;
    listenersAttached.current.add(layerId);

    map.on('mouseenter', layerId, () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', layerId, () => {
      map.getCanvas().style.cursor = '';
      clearHover(map);
    });

    map.on('mousemove', layerId, (e) => {
      if (!e.features?.length) return;
      const feat = e.features[0];
      const fid = feat.properties?._id || String(feat.id);

      if (
        hoveredFeatureId.current
        && (
          hoveredFeatureId.current !== fid
          || hoveredSourceId.current !== sourceId
          || hoveredSourceLayerId.current !== (sourceLayerId || null)
        )
      ) {
        clearHover(map);
      }
      hoveredFeatureId.current = fid;
      hoveredSourceId.current = sourceId;
      hoveredSourceLayerId.current = sourceLayerId || null;
      try {
        map.setFeatureState(
          { source: sourceId, ...(sourceLayerId ? { sourceLayer: sourceLayerId } : {}), id: fid },
          { hover: true }
        );
      } catch { /* noop */ }

      // Tooltip
      const label = labelFn(feat.properties || {});
      if (!popupRef.current) {
        popupRef.current = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          className: 'mapper-tooltip',
          offset: 15,
        });
      }
      // FIX_THE_LOT_20260913 (F-V4-3): labels come from feature properties (data plane) — render them as text
      // via DOM, never through setHTML's sanitizer (GHSA: DOM.sanitize() bypass in maplibre-gl <=6.4.0).
      const tooltipEl = document.createElement('div');
      tooltipEl.className = 'text-xs font-medium px-2 py-1';
      tooltipEl.textContent = String(label ?? '');
      popupRef.current
        .setLngLat(e.lngLat)
        .setDOMContent(tooltipEl)
        .addTo(map);
    });

    map.on('click', layerId, (e) => {
      e.preventDefault();
      if (!e.features?.length) return;
      const feat = e.features[0];
      const fid = feat.properties?._id || String(feat.id);
      onClick(fid, e.lngLat);
    });
  }, [clearHover]);

  /* ---------------------------------------------------------------- */
  /*  Probe static cadastral PMTiles availability (graceful fallback)   */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;
    void probeCadastralPmtilesAvailable().then((ok) => {
      if (cancelled) return;
      cadastralSourceProbeRef.current = ok;
      if (!ok) {
        cadastralUsesLegacyTilesRef.current = true;
        setCadastralStaticHint('Cadastral cache not found — using live tile fallback.');
        console.warn('[mapper] PMTiles HEAD failed; legacy /cp/mapper/tiles fallback active');
      }
      setCadastralProbeDone(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------------------------------------------------------------- */
  /*  SA admin navigation boundaries (country + provinces, always on)   */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const adminSourceId = 'sa-admin-source';
    const countryLineId = 'sa-country-outline';
    const provinceLineId = 'sa-province-outline';
    const provinceLabelId = 'sa-province-labels';

    // Idempotent add. Returns true once the layers are present.
    //
    // NB: this used to run once on map-instance creation gated only on
    // map.isStyleLoaded(). isStyleLoaded() also reflects in-flight *source*
    // tile loads, so it frequently reads false at the moment a single-run
    // effect fires — the effect bailed and never re-ran (deps are [storeMap]),
    // leaving the boundary layer intermittently blank. We now (re)attempt on
    // 'styledata', which also covers basemap switches (setStyle wipes custom
    // sources/layers).
    const addBoundaries = (): boolean => {
      if (!map.isStyleLoaded()) return false;

      if (!map.getSource(adminSourceId)) {
        map.addSource(adminSourceId, {
          type: 'geojson',
          data: saAdminBoundaries as GeoJSON.FeatureCollection,
        });
      }

      const beforeParcels = map.getLayer('parcels-fill') ? 'parcels-fill' : undefined;

      if (!map.getLayer(countryLineId)) {
        map.addLayer({
          id: countryLineId,
          type: 'line',
          source: adminSourceId,
          filter: ['==', ['get', 'admin_level'], 'country'],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            // Subtle near-white national outline, always on.
            'line-color': '#f8fafc',
            'line-width': ['interpolate', ['linear'], ['zoom'], 4, 1.4, 8, 2.2, 12, 2.6],
            'line-opacity': 0.7,
          },
        }, beforeParcels);
      }
      if (!map.getLayer(provinceLineId)) {
        map.addLayer({
          id: provinceLineId,
          type: 'line',
          source: adminSourceId,
          filter: ['==', ['get', 'admin_level'], 'province'],
          layout: { 'line-join': 'round' },
          paint: {
            // Thin, contrasting province borders (no fill), always on, no zoom gate.
            'line-color': '#dbe4f0',
            'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.9, 8, 1.4, 12, 2],
            'line-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.7, 9, 0.8, 12, 0.55],
          },
        }, beforeParcels);
      }
      if (!map.getLayer(provinceLabelId)) {
        map.addLayer({
          id: provinceLabelId,
          type: 'symbol',
          source: adminSourceId,
          filter: ['==', ['get', 'admin_level'], 'province'],
          layout: {
            'text-field': ['get', 'name'],
            // 'Noto Sans' is what the configured glyph endpoint serves; 'Open Sans' 404s.
            'text-font': ['Noto Sans Regular'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 4, 12, 7, 13, 10, 14],
            'text-transform': 'uppercase',
            'text-letter-spacing': 0.06,
            'text-allow-overlap': false,
            // Anchor labels at the province polygon centroid / pole of inaccessibility.
            'symbol-placement': 'point',
          },
          paint: {
            'text-color': '#eef2f8',
            'text-halo-color': '#0f172a',
            'text-halo-width': 1.4,
            // Readable zoomed out, fade out as cadastral parcels resolve in (z12+).
            'text-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.95, 10, 0.6, 12.5, 0],
          },
        }, beforeParcels);
      }
      return true;
    };

    // Add now if the style is already ready, and keep listening on 'styledata'
    // so we (re)add when the style finishes loading or the basemap is swapped.
    addBoundaries();
    const onStyleData = () => { addBoundaries(); };
    map.on('styledata', onStyleData);
    return () => {
      map.off('styledata', onStyleData);
    };
  }, [storeMap]);

  /* ---------------------------------------------------------------- */
  /*  Sync parcels to map (bottom layer — property boundaries)          */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded() || !cadastralProbeDone) return;

    const sourceId = 'parcels-source';
    const knownSourceId = 'parcels-known-source';
    const sourceLayerId = 'parcels';
    const fillId = 'parcels-fill';
    const haloId = 'parcels-halo';
    const glowId = 'parcels-glow';
    const lineId = 'parcels-line';
    const selectedLineId = 'parcels-selected-line';
    const labelId = 'parcels-label';
    const activeLabelId = 'parcels-active-label';
    const knownFillId = 'parcels-known-fill';
    const knownLineId = 'parcels-known-line';
    const parcelVectorLayerIds = [fillId, haloId, glowId, lineId, selectedLineId, labelId, activeLabelId];

    // Keep the selected parcel visible even when the full boundaries overlay is off.
    const visibility = shouldShowParcelLayers ? 'visible' : 'none';
    const mapZoom = map.getZoom();
    const showKnownParcels = shouldShowParcelLayers && knownParcels.length > 0 && mapZoom < BOUNDARY_ZOOM_THRESHOLD;
    const knownVisibility = showKnownParcels ? 'visible' : 'none';
    for (const lid of parcelVectorLayerIds) {
      if (map.getLayer(lid)) map.setLayoutProperty(lid, 'visibility', visibility);
    }
    for (const lid of [knownFillId, knownLineId]) {
      if (map.getLayer(lid)) map.setLayoutProperty(lid, 'visibility', knownVisibility);
    }

    const activeParcelId = activeParcel?.id;
    const selectedParcelIds = selectedParcels.map((parcel) => parcel.id);
    const farmParcelIds = Array.from(new Set(activeFarmParcelIds));
    const effectiveFarmOnlyMode = farmOnlyMode && farmParcelIds.length > 0;
    // Base case shows every parcel; the source minzoom gates by zoom, so the filter
    // never depends on React zoom state (which previously got stuck and hid everything).
    // Only farm-only mode restricts to specific ids.
    const restrictedParcelIds = effectiveFarmOnlyMode
      ? Array.from(new Set([...farmParcelIds, ...selectedParcelIds]))
      : null;
    const baseParcelFilter = restrictedParcelIds === null
      ? ['has', '_id']
      : restrictedParcelIds.length > 0
        ? ['match', ['get', '_id'], restrictedParcelIds, true, false]
        : ['==', ['get', '_id'], '__none__'];
    const selectedParcelFilter = selectedParcelIds.length > 0
      ? ['match', ['get', '_id'], selectedParcelIds, true, false]
      : ['==', ['get', '_id'], '__none__'];
    const guidedFillOpacity = guidedParcelSelectionActive ? 0.14 : farmOnlyMode ? 0.1 : 0.18;
    const guidedHoverFillOpacity = guidedParcelSelectionActive ? 0.24 : farmOnlyMode ? 0.16 : 0.28;
    const baseBoundaryColor = guidedParcelSelectionActive ? '#22d3ee' : farmOnlyMode ? '#10b981' : '#bef264';
    const baseBoundaryFill = guidedParcelSelectionActive ? '#14b8a6' : farmOnlyMode ? '#34d399' : '#84cc16';
    const boundaryHaloColor = guidedParcelSelectionActive ? '#083344' : '#020617';
    const guidedLineOpacity = guidedParcelSelectionActive ? 0.96 : 1;
    const guidedHoverLineOpacity = guidedParcelSelectionActive ? 0.96 : 1;
    const knownGeoJSON = parcelsToGeoJSON(knownParcels);
    const knownFillColor = selectedParcelIds.length > 0
      ? [
        'case',
        ['==', ['get', '_id'], activeParcelId || ''], '#14b8a6',
        ['match', ['get', '_id'], selectedParcelIds, true, false], '#22d3ee',
        farmOnlyMode ? '#34d399' : baseBoundaryFill,
      ]
      : [
        'case',
        ['==', ['get', '_id'], activeParcelId || ''], '#14b8a6',
        farmOnlyMode ? '#34d399' : baseBoundaryFill,
      ];
    const knownLineColor = selectedParcelIds.length > 0
      ? [
        'case',
        ['==', ['get', '_id'], activeParcelId || ''], '#14b8a6',
        ['match', ['get', '_id'], selectedParcelIds, true, false], '#67e8f9',
        farmOnlyMode ? '#34d399' : baseBoundaryColor,
      ]
      : [
        'case',
        ['==', ['get', '_id'], activeParcelId || ''], '#14b8a6',
        farmOnlyMode ? '#34d399' : baseBoundaryColor,
      ];
    const knownLineWidth = selectedParcelIds.length > 0
      ? [
        'case',
        ['==', ['get', '_id'], activeParcelId || ''], 4.75,
        ['match', ['get', '_id'], selectedParcelIds, true, false], 3.5,
        2.5,
      ]
      : [
        'case',
        ['==', ['get', '_id'], activeParcelId || ''], 4.75,
        2.5,
      ];
    // zoom-interpolated width with hover handled inside each stop (zoom must be top-level input)
    const boundaryLineWidth = [
      'interpolate', ['linear'], ['zoom'],
      9,  ['case', ['boolean', ['feature-state', 'hover'], false], 3.25, 2.25],
      12, ['case', ['boolean', ['feature-state', 'hover'], false], 5,    3.75],
      15, ['case', ['boolean', ['feature-state', 'hover'], false], 6.25, 5.25],
      18, ['case', ['boolean', ['feature-state', 'hover'], false], 7.5,  6.5],
    ];
    const boundaryHaloLineWidth = [
      'interpolate', ['linear'], ['zoom'],
      9, 4.5,
      12, 6.5,
      15, 8.5,
      18, 10.5,
    ];

    // Static PMTiles national base (preferred) or legacy on-demand tiles (fallback).
    if (!map.getSource(sourceId)) {
      if (cadastralUsesLegacyTilesRef.current) {
        map.addSource(sourceId, {
          type: 'vector',
          tiles: [getMapperTileUrlTemplate()],
          promoteId: { [sourceLayerId]: '_id' },
          minzoom: CADASTRAL_DISPLAY_MIN_ZOOM,
          maxzoom: 18,
        });
      } else {
        ensurePmtilesProtocolRegistered();
        map.addSource(sourceId, {
          type: 'vector',
          url: getCadastralPmtilesUrl(),
          promoteId: { [sourceLayerId]: '_id' },
        });
      }
    }

    // Fill gives parcels a visible tint without hiding the satellite base.
    if (!map.getLayer(fillId)) {
      map.addLayer({
        id: fillId,
        type: 'fill',
        source: sourceId,
        'source-layer': sourceLayerId,
        minzoom: CADASTRAL_DISPLAY_MIN_ZOOM,
        filter: baseParcelFilter as maplibregl.FilterSpecification,
        paint: {
          'fill-color': [
            'case',
            ['==', ['get', '_id'], activeParcelId || ''], '#14b8a6',
            baseBoundaryFill,
          ],
          'fill-opacity': cadastralFadeOpacity([
            'case',
            ['==', ['get', '_id'], activeParcelId || ''], 0.24,
            ['boolean', ['feature-state', 'hover'], false], guidedHoverFillOpacity,
            guidedFillOpacity,
          ] as maplibregl.ExpressionSpecification),
          'fill-opacity-transition': { duration: 300 },
        },
      });
    } else {
      map.setFilter(fillId, baseParcelFilter as maplibregl.FilterSpecification);
      map.setPaintProperty(fillId, 'fill-color', [
        'case',
        ['==', ['get', '_id'], activeParcelId || ''], '#14b8a6',
        baseBoundaryFill,
      ]);
      map.setPaintProperty(fillId, 'fill-opacity', cadastralFadeOpacity([
        'case',
        ['==', ['get', '_id'], activeParcelId || ''], 0.24,
        ['boolean', ['feature-state', 'hover'], false], guidedHoverFillOpacity,
        guidedFillOpacity,
      ] as maplibregl.ExpressionSpecification));
    }

    // A dark halo behind all parcel outlines keeps borders readable over busy satellite imagery.
    if (!map.getLayer(haloId)) {
      map.addLayer({
        id: haloId,
        type: 'line',
        source: sourceId,
        'source-layer': sourceLayerId,
        minzoom: CADASTRAL_DISPLAY_MIN_ZOOM,
        filter: baseParcelFilter as maplibregl.FilterSpecification,
        paint: {
          'line-color': boundaryHaloColor,
          'line-width': boundaryHaloLineWidth,
          'line-opacity': cadastralFadeOpacity(0.58),
          'line-blur': 0.4,
        },
      });
    } else {
      map.setFilter(haloId, baseParcelFilter as maplibregl.FilterSpecification);
      map.setPaintProperty(haloId, 'line-color', boundaryHaloColor);
      map.setPaintProperty(haloId, 'line-width', boundaryHaloLineWidth);
      map.setPaintProperty(haloId, 'line-opacity', cadastralFadeOpacity(0.58));
    }

    // Active glow ring to keep the current parcel obvious on satellite imagery.
    if (!map.getLayer(glowId)) {
      map.addLayer({
        id: glowId,
        type: 'line',
        source: sourceId,
        'source-layer': sourceLayerId,
        minzoom: CADASTRAL_DISPLAY_MIN_ZOOM,
        filter: ['==', ['get', '_id'], activeParcelId || '__none__'],
        paint: {
          'line-color': '#f8fafc',
          'line-width': 8,
          'line-opacity': cadastralFadeOpacity(0.9),
          'line-blur': 2,
        },
      });
    } else {
      map.setFilter(glowId, ['==', ['get', '_id'], activeParcelId || '__none__']);
    }

    // Base outline for unselected parcels.
    if (!map.getLayer(lineId)) {
      map.addLayer({
        id: lineId,
        type: 'line',
        source: sourceId,
        'source-layer': sourceLayerId,
        minzoom: CADASTRAL_DISPLAY_MIN_ZOOM,
        filter: baseParcelFilter as maplibregl.FilterSpecification,
        paint: {
          'line-color': baseBoundaryColor,
          'line-width': boundaryLineWidth,
          'line-opacity': cadastralFadeOpacity([
            'case',
            ['boolean', ['feature-state', 'hover'], false], guidedHoverLineOpacity,
            guidedLineOpacity,
          ] as maplibregl.ExpressionSpecification),
        },
      });
    } else {
      map.setFilter(lineId, baseParcelFilter as maplibregl.FilterSpecification);
      map.setPaintProperty(lineId, 'line-color', baseBoundaryColor);
      map.setPaintProperty(lineId, 'line-width', boundaryLineWidth);
      map.setPaintProperty(lineId, 'line-opacity', cadastralFadeOpacity([
        'case',
        ['boolean', ['feature-state', 'hover'], false], guidedHoverLineOpacity,
        guidedLineOpacity,
      ] as maplibregl.ExpressionSpecification));
    }

    // Selected parcel overlay keeps the existing highlighted cyan dashed treatment.
    if (!map.getLayer(selectedLineId)) {
      map.addLayer({
        id: selectedLineId,
        type: 'line',
        source: sourceId,
        'source-layer': sourceLayerId,
        minzoom: CADASTRAL_DISPLAY_MIN_ZOOM,
        filter: selectedParcelFilter as maplibregl.FilterSpecification,
        paint: {
          'line-color': '#67e8f9',
          'line-width': 4.5,
          'line-dasharray': [4, 3],
          'line-opacity': 1,
        },
      });
    } else {
      map.setFilter(selectedLineId, selectedParcelFilter as maplibregl.FilterSpecification);
    }

    // Labels — 'Noto Sans' is what the configured glyph endpoint actually serves; the old
    // 'Open Sans' 404s, and an unresolved font silently fails glyph load which poisons the
    // entire tile so fill/line buckets come back empty. Added in the same pass as geometry.
    if (!map.getLayer(labelId)) {
      map.addLayer({
        id: labelId,
        type: 'symbol',
        source: sourceId,
        'source-layer': sourceLayerId,
        minzoom: CADASTRAL_LABEL_MIN_ZOOM,
        filter: baseParcelFilter as maplibregl.FilterSpecification,
        layout: {
          visibility,
          'text-field': ['coalesce', ['get', '_label'], ['get', '_feature_name'], ['get', '_farm_name'], ['get', '_external_id'], ['get', '_sg_number']],
          'text-font': ['Noto Sans Regular'],
          'text-size': 11,
          'text-anchor': 'center',
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#0f172a',
          'text-halo-width': 1.2,
          'text-opacity': cadastralLabelFadeOpacity(),
        },
      });
    } else {
      map.setLayoutProperty(labelId, 'visibility', visibility);
      map.setFilter(labelId, baseParcelFilter as maplibregl.FilterSpecification);
      map.setPaintProperty(labelId, 'text-opacity', cadastralLabelFadeOpacity());
    }
    if (!map.getLayer(activeLabelId)) {
      map.addLayer({
        id: activeLabelId,
        type: 'symbol',
        source: sourceId,
        'source-layer': sourceLayerId,
        minzoom: CADASTRAL_LABEL_MIN_ZOOM,
        filter: ['==', ['get', '_id'], activeParcelId || '__none__'],
        layout: {
          visibility,
          'text-field': ['coalesce', ['get', '_label'], ['get', '_feature_name'], ['get', '_farm_name'], ['get', '_external_id'], ['get', '_sg_number']],
          'text-size': 13,
          'text-font': ['Noto Sans Bold'],
          'text-anchor': 'top',
          'text-offset': [0, -0.4],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#ecfeff',
          'text-halo-color': '#042f2e',
          'text-halo-width': 2,
          'text-opacity': 1,
        },
      });
    } else {
      map.setLayoutProperty(activeLabelId, 'visibility', visibility);
      map.setFilter(activeLabelId, ['==', ['get', '_id'], activeParcelId || '__none__']);
    }

    // Click listeners
    for (const lid of [fillId, lineId, selectedLineId]) {
      addLayerListeners(
        map, lid, sourceId,
        (fid) => {
          if (guidedParcelSelectionActiveRef.current) {
            // Guided setup resolves parcels through the generic map click path so
            // clicks on parcel layers and raw map clicks behave the same way.
            return;
          }
          void toggleResolvedParcelSelection(fid);
        },
        (props) => {
          const name = String(props._label || props._feature_name || props._farm_name || props._external_id || props._sg_number || 'Parcel');
          const area = props._area_ha ? ` (${Number(props._area_ha).toFixed(1)} ha)` : '';
          return `${name}${area}`;
        },
        sourceLayerId,
      );
    }

    const knownExisting = map.getSource(knownSourceId) as maplibregl.GeoJSONSource | undefined;
    if (knownExisting) {
      knownExisting.setData(knownGeoJSON);
    } else {
      map.addSource(knownSourceId, {
        type: 'geojson',
        data: knownGeoJSON,
      });
    }

    if (!map.getLayer(knownFillId)) {
      map.addLayer({
        id: knownFillId,
        type: 'fill',
        source: knownSourceId,
        layout: {
          visibility: knownVisibility,
        },
        paint: {
          'fill-color': knownFillColor,
          'fill-opacity': [
            'case',
            ['==', ['get', '_id'], activeParcelId || ''], 0.22,
            ['boolean', ['feature-state', 'hover'], false], farmOnlyMode ? 0.16 : 0.18,
            farmOnlyMode ? 0.1 : 0.12,
          ],
          'fill-opacity-transition': { duration: 200 },
        },
      });
    } else {
      map.setLayoutProperty(knownFillId, 'visibility', knownVisibility);
      map.setPaintProperty(knownFillId, 'fill-color', knownFillColor as maplibregl.ExpressionSpecification);
      map.setPaintProperty(knownFillId, 'fill-opacity', [
        'case',
        ['==', ['get', '_id'], activeParcelId || ''], 0.22,
        ['boolean', ['feature-state', 'hover'], false], farmOnlyMode ? 0.16 : 0.18,
        farmOnlyMode ? 0.1 : 0.12,
      ]);
    }
    if (!map.getLayer(knownLineId)) {
      map.addLayer({
        id: knownLineId,
        type: 'line',
        source: knownSourceId,
        layout: {
          visibility: knownVisibility,
        },
        paint: {
          'line-color': knownLineColor,
          'line-width': knownLineWidth,
          'line-opacity': [
            'case',
            ['boolean', ['feature-state', 'hover'], false], 1,
            0.94,
          ],
        },
      });
    } else {
      map.setLayoutProperty(knownLineId, 'visibility', knownVisibility);
      map.setPaintProperty(knownLineId, 'line-color', knownLineColor as maplibregl.ExpressionSpecification);
      map.setPaintProperty(knownLineId, 'line-width', knownLineWidth as maplibregl.ExpressionSpecification);
      map.setPaintProperty(knownLineId, 'line-opacity', [
        'case',
        ['boolean', ['feature-state', 'hover'], false], 1,
        0.94,
      ]);
    }

    for (const lid of [knownFillId, knownLineId]) {
      addLayerListeners(
        map, lid, knownSourceId,
        (fid) => {
          if (guidedParcelSelectionActiveRef.current) {
            return;
          }
          void toggleResolvedParcelSelection(fid);
        },
        (props) => {
          const name = String(props._label || props._feature_name || props._farm_name || props._external_id || props._sg_number || 'Parcel');
          const area = props._area_ha ? ` (${Number(props._area_ha).toFixed(1)} ha)` : '';
          return `${name}${area}`;
        },
      );
    }
  }, [BOUNDARY_ZOOM_THRESHOLD, CADASTRAL_DISPLAY_MIN_ZOOM, CADASTRAL_LABEL_MIN_ZOOM, activeFarmParcelIds, activeFarmParcelIdsKey, activeParcel, addLayerListeners, cadastralProbeDone, currentZoom, farmOnlyMode, guidedParcelSelectionActive, knownParcels, selectedParcels, shouldShowParcelLayers, storeMap, toggleResolvedParcelSelection]);

  /* ---------------------------------------------------------------- */
  /*  Live claims + self-mapped overlay (JWT-gated, above static base)  */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded() || !shouldShowParcelLayers) return;

    const overlaySourceId = 'parcels-overlay-source';
    const overlayFillId = 'parcels-overlay-fill';
    const overlayLineId = 'parcels-overlay-line';
    const emptyCollection: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

    if (!map.getSource(overlaySourceId)) {
      map.addSource(overlaySourceId, { type: 'geojson', data: emptyCollection });
    }
    const beforeSelected = map.getLayer('parcels-selected-line') ? 'parcels-selected-line' : undefined;
    if (!map.getLayer(overlayFillId)) {
      map.addLayer({
        id: overlayFillId,
        type: 'fill',
        source: overlaySourceId,
        paint: {
          'fill-color': [
            'match', ['get', '_overlay_type'],
            'promoted', '#14b8a6',
            'self_mapped', '#22d3ee',
            '#06b6d4',
          ],
          'fill-opacity': 0.28,
        },
      }, beforeSelected);
    }
    if (!map.getLayer(overlayLineId)) {
      map.addLayer({
        id: overlayLineId,
        type: 'line',
        source: overlaySourceId,
        paint: {
          'line-color': [
            'match', ['get', '_overlay_type'],
            'promoted', '#5eead4',
            'self_mapped', '#67e8f9',
            '#22d3ee',
          ],
          'line-width': 4,
          'line-opacity': 0.95,
        },
      }, beforeSelected);
    }

    const syncOverlay = () => {
      if (map.getZoom() < CADASTRAL_DISPLAY_MIN_ZOOM) {
        const src = map.getSource(overlaySourceId) as maplibregl.GeoJSONSource | undefined;
        src?.setData(emptyCollection);
        return;
      }
      const bounds = map.getBounds();
      if (!bounds) return;
      const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
      void fetchParcelOverlayGeoJSON(bbox)
        .then((data) => {
          const src = map.getSource(overlaySourceId) as maplibregl.GeoJSONSource | undefined;
          src?.setData(data);
        })
        .catch((err) => {
          console.warn('[mapper] parcel overlay fetch failed', err);
        });
    };

    syncOverlay();
    const handleMove = () => {
      if (parcelOverlaySyncTimeoutRef.current !== null) {
        window.clearTimeout(parcelOverlaySyncTimeoutRef.current);
      }
      parcelOverlaySyncTimeoutRef.current = window.setTimeout(() => {
        parcelOverlaySyncTimeoutRef.current = null;
        syncOverlay();
      }, 350);
    };
    map.on('moveend', handleMove);
    return () => {
      map.off('moveend', handleMove);
      if (parcelOverlaySyncTimeoutRef.current !== null) {
        window.clearTimeout(parcelOverlaySyncTimeoutRef.current);
        parcelOverlaySyncTimeoutRef.current = null;
      }
    };
  }, [CADASTRAL_DISPLAY_MIN_ZOOM, shouldShowParcelLayers, storeMap, selectedParcels.length, activeParcel?.id]);

  /* ---------------------------------------------------------------- */
  /*  Sync layers + features to map                                     */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    // Group features by layer
    const featuresByLayer = new Map<string, MapFeature[]>();
    for (const f of features) {
      const list = featuresByLayer.get(f.layer_id) || [];
      list.push(f);
      featuresByLayer.set(f.layer_id, list);
    }

    // Add/update sources and layers for each map layer
    for (const layer of layers) {
      const sourceId = `layer-${layer.id}`;
      const fillId = `fill-${layer.id}`;
      const lineId = `line-${layer.id}`;
      const pointId = `point-${layer.id}`;

      const layerFeatures = featuresByLayer.get(layer.id) || [];
      const geojson = featuresToGeoJSON(layerFeatures);

      // Source
      const existingSource = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
      if (existingSource) {
        existingSource.setData(geojson);
      } else {
        map.addSource(sourceId, { type: 'geojson', data: geojson });
      }

      const visibility = layer.visible ? 'visible' : 'none';
      const color = layer.style?.color || '#3b82f6';
      const fillOpacity = layer.style?.fillOpacity ?? 0.25;
      const weight = layer.style?.weight ?? 2;

      // Fill layer (polygons)
      if (!map.getLayer(fillId)) {
        map.addLayer({
          id: fillId,
          type: 'fill',
          source: sourceId,
          filter: ['any', ['==', '$type', 'Polygon']],
          paint: {
            'fill-color': color,
            'fill-opacity': [
              'case',
              ['boolean', ['feature-state', 'hover'], false], Math.min(fillOpacity + 0.2, 0.7),
              ['boolean', ['feature-state', 'selected'], false], Math.min(fillOpacity + 0.3, 0.8),
              fillOpacity,
            ],
            'fill-opacity-transition': { duration: 300 },
          },
          layout: { visibility },
        });
      } else {
        map.setLayoutProperty(fillId, 'visibility', visibility);
        map.setPaintProperty(fillId, 'fill-color', color);
      }

      // Line layer (outlines + lines)
      if (!map.getLayer(lineId)) {
        map.addLayer({
          id: lineId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': color,
            'line-width': [
              'case',
              ['boolean', ['feature-state', 'hover'], false], weight + 2,
              ['boolean', ['feature-state', 'selected'], false], weight + 3,
              weight,
            ],
            'line-opacity': 0.9,
            'line-opacity-transition': { duration: 300 },
          },
          layout: { visibility },
        });
      } else {
        map.setLayoutProperty(lineId, 'visibility', visibility);
        map.setPaintProperty(lineId, 'line-color', color);
      }

      // Point layer (markers)
      if (!map.getLayer(pointId)) {
        map.addLayer({
          id: pointId,
          type: 'circle',
          source: sourceId,
          filter: ['==', '$type', 'Point'],
          paint: {
            'circle-radius': [
              'case',
              ['boolean', ['feature-state', 'hover'], false], 10,
              ['boolean', ['feature-state', 'selected'], false], 12,
              7,
            ],
            'circle-color': color,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
            'circle-opacity': 0.9,
            'circle-opacity-transition': { duration: 300 },
          },
          layout: { visibility },
        });
      } else {
        map.setLayoutProperty(pointId, 'visibility', visibility);
        map.setPaintProperty(pointId, 'circle-color', color);
      }

      // Click listeners for features
      for (const lid of [fillId, lineId, pointId]) {
        addLayerListeners(
          map, lid, sourceId,
          (fid, lngLat) => {
            const matched = features.find((f) => f.id === fid);
            if (matched) {
              setSelectedFeature(matched);
              try { map.setFeatureState({ source: sourceId, id: fid }, { selected: true }); } catch { /* noop */ }
              map.flyTo({
                center: [lngLat.lng, lngLat.lat],
                zoom: Math.max(map.getZoom(), 14),
                duration: 1500,
                curve: 1.42,
              });
            }
          },
          (props) => String(props._name || 'Feature'),
        );
      }
    }

    // Clean up removed layers
    const currentSourceIds = new Set(layers.map((l) => `layer-${l.id}`));
    const style = map.getStyle();
    if (style?.layers) {
      for (const sl of style.layers) {
        if (
          (sl.id.startsWith('fill-') || sl.id.startsWith('line-') || sl.id.startsWith('point-')) &&
          'source' in sl &&
          typeof sl.source === 'string' &&
          sl.source.startsWith('layer-') &&
          !currentSourceIds.has(sl.source)
        ) {
          map.removeLayer(sl.id);
          listenersAttached.current.delete(sl.id);
        }
      }
    }
    if (style?.sources) {
      for (const sid of Object.keys(style.sources)) {
        if (sid.startsWith('layer-') && !currentSourceIds.has(sid)) {
          map.removeSource(sid);
        }
      }
    }
  }, [layers, features, setSelectedFeature, addLayerListeners]);

  /* ---------------------------------------------------------------- */
  /*  Sync infrastructure to map (rendered above feature layers)        */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const sourceId = 'infra-source';
    const fillId = 'infra-fill';
    const lineId = 'infra-line';
    const pointId = 'infra-point';
    const labelId = 'infra-label';

    const geojson = infrastructureToGeoJSON(infrastructure);

    // Source
    const existing = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
    if (existing) {
      existing.setData(geojson);
    } else {
      map.addSource(sourceId, { type: 'geojson', data: geojson });
    }

    // Fill (polygons) — category-coloured
    if (!map.getLayer(fillId)) {
      map.addLayer({
        id: fillId,
        type: 'fill',
        source: sourceId,
        filter: ['any', ['==', '$type', 'Polygon']],
        paint: {
          'fill-color': ['get', '_color'],
          'fill-opacity': [
            'case',
            ['boolean', ['feature-state', 'hover'], false], 0.45,
            ['boolean', ['feature-state', 'selected'], false], 0.55,
            0.3,
          ],
          'fill-opacity-transition': { duration: 300 },
        },
      });
    }

    // Line (outlines)
    if (!map.getLayer(lineId)) {
      map.addLayer({
        id: lineId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': ['get', '_color'],
          'line-width': [
            'case',
            ['boolean', ['feature-state', 'hover'], false], 3.5,
            ['boolean', ['feature-state', 'selected'], false], 4,
            2,
          ],
          'line-opacity': 0.9,
        },
      });
    }

    // Point (circles for point infrastructure)
    if (!map.getLayer(pointId)) {
      map.addLayer({
        id: pointId,
        type: 'circle',
        source: sourceId,
        filter: ['==', '$type', 'Point'],
        paint: {
          'circle-radius': [
            'case',
            ['boolean', ['feature-state', 'hover'], false], 11,
            ['boolean', ['feature-state', 'selected'], false], 13,
            8,
          ],
          'circle-color': ['get', '_color'],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2.5,
          'circle-opacity': 0.9,
        },
      });
    }

    // Labels
    if (!map.getLayer(labelId)) {
      map.addLayer({
        id: labelId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'text-field': ['get', '_name'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 10,
          'text-anchor': 'top',
          'text-offset': [0, 1],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#1e293b',
          'text-halo-width': 1,
          'text-opacity': 0.9,
        },
      });
    }

    // Click listeners for infrastructure
    for (const lid of [fillId, lineId, pointId]) {
      addLayerListeners(
        map, lid, sourceId,
        (fid, lngLat) => {
          const matched = infrastructure.find((i) => i.id === fid);
          if (matched) {
            setSelectedInfrastructure(matched);
            try { map.setFeatureState({ source: sourceId, id: fid }, { selected: true }); } catch { /* noop */ }
            map.flyTo({
              center: [lngLat.lng, lngLat.lat],
              zoom: Math.max(map.getZoom(), 15),
              duration: 1500,
              curve: 1.42,
            });
          }
        },
        (props) => {
          const name = String(props._name || 'Infrastructure');
          const type = String(props._infra_type || '').split(':').pop()?.replace(/_/g, ' ') || '';
          return type ? `${name} <span style="opacity:0.6">(${type})</span>` : name;
        },
      );
    }
  }, [infrastructure, setSelectedInfrastructure, addLayerListeners]);

  /* ---------------------------------------------------------------- */
  /*  Asset-backed infrastructure overlay                              */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const sourceId = 'infra-asset-source';
    const lineId = 'infra-asset-line';
    const pointId = 'infra-asset-point';
    const fillId = 'infra-asset-fill';
    const assetGeojson = infrastructureToGeoJSON(assetBackedInfrastructure);

    const existing = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
    if (existing) {
      existing.setData(assetGeojson);
    } else {
      map.addSource(sourceId, { type: 'geojson', data: assetGeojson });
    }

    if (!map.getLayer(fillId)) {
      map.addLayer({
        id: fillId,
        type: 'fill',
        source: sourceId,
        filter: ['any', ['==', '$type', 'Polygon']],
        paint: {
          'fill-color': '#f59e0b',
          'fill-opacity': 0.12,
        },
      });
    }

    if (!map.getLayer(lineId)) {
      map.addLayer({
        id: lineId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': '#f59e0b',
          'line-width': [
            'case',
            ['boolean', ['feature-state', 'hover'], false], 5,
            3,
          ],
          'line-opacity': 0.95,
          'line-dasharray': [1.2, 0.8],
        },
      });
    }

    if (!map.getLayer(pointId)) {
      map.addLayer({
        id: pointId,
        type: 'circle',
        source: sourceId,
        filter: ['==', '$type', 'Point'],
        paint: {
          'circle-radius': 10,
          'circle-color': '#f59e0b',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2.5,
          'circle-opacity': 0.95,
        },
      });
    }

    const visibility = assetBackedInfrastructure.length > 0 ? 'visible' : 'none';
    for (const layerId of [fillId, lineId, pointId]) {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visibility);
      }
    }

    for (const lid of [fillId, lineId, pointId]) {
      addLayerListeners(
        map,
        lid,
        sourceId,
        (fid, lngLat) => {
          const matched = assetBackedInfrastructure.find((item) => item.id === fid);
          if (matched) {
            setSelectedInfrastructure(matched);
            try { map.setFeatureState({ source: sourceId, id: fid }, { selected: true }); } catch { /* noop */ }
            map.flyTo({
              center: [lngLat.lng, lngLat.lat],
              zoom: Math.max(map.getZoom(), 15),
              duration: 1200,
            });
          }
        },
        (props) => `${String(props._name || 'Infrastructure')} <span style="opacity:0.6">(asset-backed)</span>`
      );
    }
  }, [assetBackedInfrastructure, addLayerListeners, setSelectedInfrastructure]);

  /* ---------------------------------------------------------------- */
  /*  Sync GEE job state into overlay                                   */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!geeOverlay?.job_id || !geeJob.data) return;
    if (geeJob.data.id !== geeOverlay.job_id) return;

    if (
      geeJob.data.status === 'completed' &&
      geeJob.data.tile_url &&
      (geeOverlay.status !== 'completed' || geeOverlay.tile_url !== geeJob.data.tile_url)
    ) {
      updateGeeOverlay({
        status: 'completed',
        tile_url: geeJob.data.tile_url,
        error: null,
      });
      toast.success('NDVI overlay ready', {
        description: 'The Earth Engine layer is now visible on the map.',
      });
      return;
    }

    if (geeJob.data.status === 'failed' && geeOverlay.status !== 'failed') {
      updateGeeOverlay({
        status: 'failed',
        error: geeJob.data.error || 'Earth Engine analysis failed',
      });
      toast.error('NDVI analysis failed', {
        description: geeJob.data.error || 'Earth Engine analysis failed',
      });
    }
  }, [geeOverlay, geeJob.data, updateGeeOverlay]);

  /* ---------------------------------------------------------------- */
  /*  Sync GEE raster overlay to map                                    */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const sourceId = 'gee-overlay-source';
    const layerId = 'gee-overlay-layer';

    const removeOverlay = () => {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
      lastGeeTileUrlRef.current = null;
    };

    if (!geeOverlay || geeOverlay.status !== 'completed' || !geeOverlay.tile_url) {
      removeOverlay();
      return;
    }

    const tileChanged = lastGeeTileUrlRef.current !== geeOverlay.tile_url;
    if (tileChanged) {
      removeOverlay();
      map.addSource(sourceId, {
        type: 'raster',
        tiles: [geeOverlay.tile_url],
        tileSize: 256,
      });
      const beforeId = map.getLayer('parcels-fill')
        ? 'parcels-fill'
        : map.getLayer('infra-fill')
          ? 'infra-fill'
          : undefined;
      map.addLayer(
        {
          id: layerId,
          type: 'raster',
          source: sourceId,
          paint: {
            'raster-opacity': geeOverlay.opacity,
            'raster-resampling': 'linear',
          },
        },
        beforeId
      );
      lastGeeTileUrlRef.current = geeOverlay.tile_url;
      return;
    }

    if (map.getLayer(layerId)) {
      map.setPaintProperty(layerId, 'raster-opacity', geeOverlay.opacity);
    }
  }, [geeOverlay]);

  /* ---------------------------------------------------------------- */
  /*  Import preview overlay                                            */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const previewSourceId = 'import-preview';
    const previewFillId = 'import-preview-fill';
    const previewLineId = 'import-preview-line';
    const previewPointId = 'import-preview-point';

    if (importStep === 'preview' && importPreview) {
      const geojson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: importPreview.features.map((f, i) => ({
          type: 'Feature' as const,
          id: i,
          geometry: f.geometry as GeoJSON.Geometry,
          properties: { _name: f.name, ...f.properties },
        })),
      };

      const existing = map.getSource(previewSourceId) as maplibregl.GeoJSONSource | undefined;
      if (existing) {
        existing.setData(geojson);
      } else {
        map.addSource(previewSourceId, { type: 'geojson', data: geojson });
      }

      if (!map.getLayer(previewFillId)) {
        map.addLayer({
          id: previewFillId,
          type: 'fill',
          source: previewSourceId,
          filter: ['==', '$type', 'Polygon'],
          paint: { 'fill-color': '#a855f7', 'fill-opacity': 0.15 },
        });
      }
      if (!map.getLayer(previewLineId)) {
        map.addLayer({
          id: previewLineId,
          type: 'line',
          source: previewSourceId,
          paint: { 'line-color': '#a855f7', 'line-width': 2, 'line-dasharray': [4, 3] },
        });
      }
      if (!map.getLayer(previewPointId)) {
        map.addLayer({
          id: previewPointId,
          type: 'circle',
          source: previewSourceId,
          filter: ['==', '$type', 'Point'],
          paint: { 'circle-radius': 6, 'circle-color': '#a855f7', 'circle-stroke-color': '#fff', 'circle-stroke-width': 2, 'circle-opacity': 0.7 },
        });
      }

      // Fly to preview bounds
      if (importPreview.bounds) {
        if (!fitBoundsSafely(map, importPreview.bounds, { duration: 2000, curve: 1.2 })) {
          map.flyTo({
            center: getBoundsCenter(importPreview.bounds),
            zoom: Math.max(map.getZoom(), 12),
            duration: 2000,
            curve: 1.2,
            essential: true,
          });
        }
      }
    } else {
      // Remove preview layers
      [previewFillId, previewLineId, previewPointId].forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      if (map.getSource(previewSourceId)) map.removeSource(previewSourceId);
    }
  }, [importPreview, importStep]);

  /* ---------------------------------------------------------------- */
  /*  FlyTo handler                                                     */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !flyToTarget) return;

    if (flyToTarget.bounds) {
      if (!fitBoundsSafely(map, flyToTarget.bounds, { duration: 2500, curve: 1.42 })) {
        // Very narrow viewports can reject fitBounds; fall back to a centered flyTo.
        map.flyTo({
          center: flyToTarget.center,
          zoom: flyToTarget.zoom ?? Math.max(map.getZoom(), 14),
          duration: 2500,
          curve: 1.42,
          essential: true,
        });
      }
    } else {
      map.flyTo({
        center: flyToTarget.center,
        zoom: flyToTarget.zoom ?? 14,
        duration: 2500,
        curve: 1.42,
        essential: true,
      });
    }

    clearFlyTo();
  }, [flyToTarget, clearFlyTo]);

  /* ---------------------------------------------------------------- */
  /*  Drag-and-drop device linking                                      */
  /* ---------------------------------------------------------------- */
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const deviceId = e.dataTransfer.getData('application/x-iot-device-id');
    if (!deviceId) return;

    const map = mapRef.current;
    if (!map) return;

    // Find the infrastructure object nearest the drop point
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const point: [number, number] = [e.clientX - rect.left, e.clientY - rect.top];

    // Query rendered features from the infrastructure layers
    const infraLayers = ['infra-fill', 'infra-line', 'infra-point'].filter(id => map.getLayer(id));
    const hits = map.queryRenderedFeatures(point, { layers: infraLayers });

    if (hits.length > 0) {
      const fid = hits[0].properties?._id;
      const matched = infrastructure.find((i) => i.id === fid);
      if (matched) {
        linkDevice.mutate(
          { infraId: matched.id, device_id: deviceId },
          {
            onSuccess: () => toast.success(`Device ${deviceId} linked to ${matched.name}`),
            onError: (err) => toast.error('Link failed', { description: String(err) }),
          },
        );
        return;
      }
    }

    toast.warning('Drop device onto an infrastructure object to link it');
  }, [infrastructure, linkDevice]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('application/x-iot-device-id')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'link';
    }
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Render                                                            */
  /* ---------------------------------------------------------------- */
  return (
    <div
      className="relative h-full w-full bg-slate-100"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <div ref={containerRef} className="absolute inset-0" />

      {/* GEE overlay control */}
      {geeOverlay && (
        <div className="absolute top-3 right-3 z-20 w-72 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 px-3 py-3">
          <div className="flex items-start gap-2.5">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              geeOverlay.status === 'failed'
                ? 'bg-rose-100 text-rose-600'
                : 'bg-emerald-100 text-emerald-700'
            }`}>
              {geeOverlay.status === 'pending' ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Leaf className="w-4 h-4" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-gray-900 truncate">{geeOverlay.title}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {geeOverlay.status === 'pending' && 'Running Earth Engine NDVI analysis...'}
                {geeOverlay.status === 'completed' && (geeOverlay.subtitle || 'Raster overlay loaded on the map')}
                {geeOverlay.status === 'failed' && (geeOverlay.error || 'Earth Engine analysis failed')}
              </div>
            </div>
            <button
              onClick={() => clearGeeOverlay()}
              className="p-1 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              title="Close overlay"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {geeOverlay.status === 'completed' && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-gray-400 mb-1">
                <span>Opacity</span>
                <span>{Math.round(geeOverlay.opacity * 100)}%</span>
              </div>
              <input
                type="range"
                min={20}
                max={95}
                step={5}
                value={Math.round(geeOverlay.opacity * 100)}
                onChange={(e) => updateGeeOverlay({ opacity: Number(e.target.value) / 100 })}
                className="w-full accent-emerald-600"
              />
            </div>
          )}
        </div>
      )}

      {cadastralStaticHint && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 max-w-md rounded-lg bg-amber-950/88 px-3 py-2 text-[11px] text-amber-100 shadow-lg backdrop-blur-sm">
          {cadastralStaticHint} Header Healthy is process chrome — this cache miss stays visible.
        </div>
      )}

      {guidedParcelSelectionActive && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 rounded-full bg-slate-950/82 px-4 py-2 text-xs font-medium text-white shadow-lg backdrop-blur-sm">
          {lookupParcelByPoint.isPending
            ? 'Finding the farm parcel at that point...'
            : activeParcel
              ? 'Parcel matched. Check the green parcel card in the setup panel.'
              : 'Click once on the farm. Search for a town first if you need to move the map.'}
        </div>
      )}

      {/* Boundary zoom hint */}
      {!guidedParcelSelectionActive && !activeParcel && !hasSelectedParcels && currentZoom < CADASTRAL_DISPLAY_MIN_ZOOM && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 bg-teal-900/80 backdrop-blur-sm text-teal-100 text-xs font-medium px-4 py-2 rounded-full shadow-lg">
          Zoom in — province outlines and farm parcels resolve gradually as you approach the land
        </div>
      )}

    </div>
  );
}
